import { useEffect, useState } from 'react';
import { ViewModeFieldset } from '@/components/ViewModeFieldset';
import { Pencil } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Field, Select, todayIso, useFormErrors } from '@/components/live';
import { DropZone } from '@/components/DropZone';
import { FarmerPhoto, usePhotoUrls } from '@/components/FarmerPhoto';
import { RecordDrawer } from '@/components/RecordDrawer';
import { DeleteRecordButton } from '@/components/DeleteRecordButton';
import { createRow, saveRow } from '@/api/resources';
import { farmerOptions, plotOptions, referenceValues } from '@/api/lookups';
import { uploadDocument } from '@/api/documents';
import { useAuth } from '@/auth/AuthProvider';
import { can, normaliseIdentifier } from '@/auth/session';
import { toast } from '@/lib/toast';
import { isSupabaseConfigured } from '@/lib/supabase';

export interface StakeholderRow {
  id: string;
  stakeholder_code: string | null;
  farmer_id: string | null;
  farmer_name: string | null;
  farmer_code: string | null;
  plot_id: string | null;
  plot_code: string | null;
  village_name: string | null;
  full_name: string;
  stakeholder_type: string;
  role_type_id: string | null;
  role_label: string | null;
  relation_to_farm: string | null;
  organisation_name: string | null;
  phone: string | null;
  email: string | null;
  photo_document_id: string | null;
  interest: string | null;
  last_consulted_on: string | null;
  notes: string | null;
  status: string;
  boundary_side: string | null;
  neighbour_farmer_id: string | null;
  neighbour_farmer_name: string | null;
  neighbour_farmer_code: string | null;
  is_indigenous: boolean | null;
  indigenous_community: string | null;
  stakeholder_level: 'organisation' | 'farmer' | 'plot';
}

/** Which side of the plot a neighbour's land lies on (migration 0061). */
export const BOUNDARY_SIDES = [
  { id: '', label: '--' },
  { id: 'north', label: 'North' }, { id: 'north_east', label: 'North-east' },
  { id: 'east', label: 'East' }, { id: 'south_east', label: 'South-east' },
  { id: 'south', label: 'South' }, { id: 'south_west', label: 'South-west' },
  { id: 'west', label: 'West' }, { id: 'north_west', label: 'North-west' },
];

export function sideLabel(id?: string | null): string {
  return BOUNDARY_SIDES.find((entry) => entry.id === id && id)?.label ?? '--';
}

export const STAKEHOLDER_TYPES = [
  { id: 'individual', label: 'Individual' },
  { id: 'organisation', label: 'Organisation' },
  { id: 'government', label: 'Government' },
  { id: 'community_group', label: 'Community group' },
];

export const RELATIONS = [
  { id: '', label: '--' },
  { id: 'adjoining_land', label: 'Holds adjoining land' },
  { id: 'right_of_way', label: 'Uses a path / road through the farm' },
  { id: 'works_on_farm', label: 'Works on the farm' },
  { id: 'buys_produce', label: 'Buys the produce' },
  { id: 'supplies_inputs', label: 'Supplies inputs / services' },
  { id: 'shares_water', label: 'Shares a water source' },
  { id: 'cultural_site', label: 'Visits a cultural / sacred site on it' },
  { id: 'regulates', label: 'Regulates or inspects' },
  { id: 'supports', label: 'Supports / advises' },
  { id: 'other', label: 'Other' },
];

export function relationLabel(id?: string | null): string {
  return RELATIONS.find((entry) => entry.id === id)?.label ?? '--';
}

const EMPTY = {
  full_name: '', stakeholder_type: 'individual', role_type_id: '', relation_to_farm: '', organisation_name: '',
  phone: '', email: '', farmer_id: '', plot_id: '', interest: '', last_consulted_on: '', notes: '',
  boundary_side: '', neighbour_farmer_id: '', is_indigenous: '' as '' | 'yes' | 'no', indigenous_community: '',
  level: 'farmer' as 'organisation' | 'farmer',
};

/**
 * Add or edit a farm stakeholder (owner, 2026-09-21): who they are, how to reach them, their
 * photograph, their role and their relation to THIS farm - against the farmer and, where it is
 * specific, the plot.
 */
export function StakeholderDrawer({
  open,
  onClose,
  editing,
  farmerId,
  plotId,
  neighbour,
}: {
  open: boolean;
  onClose: () => void;
  editing?: StakeholderRow | null;
  /** Fixes the farmer - used from a farmer's own record. */
  farmerId?: string;
  /** Fixes the plot - used from a plot's own record. */
  plotId?: string;
  /** Opens as "add a neighbour": relation preset to adjoining land, with the side asked first. */
  neighbour?: boolean;
}) {
  const { profile } = useAuth();
  const mayWrite = can(profile, 'party.register', editing ? 'update' : 'create');
  const queryClient = useQueryClient();
  const { errors, check, clear } = useFormErrors();
  const [form, setForm] = useState(() => editing ? {
    full_name: editing.full_name, stakeholder_type: editing.stakeholder_type, role_type_id: editing.role_type_id ?? '',
    relation_to_farm: editing.relation_to_farm ?? '', organisation_name: editing.organisation_name ?? '',
    phone: editing.phone ?? '', email: editing.email ?? '', farmer_id: editing.farmer_id ?? farmerId ?? '',
    plot_id: editing.plot_id ?? '', interest: editing.interest ?? '', last_consulted_on: editing.last_consulted_on ?? '',
    notes: editing.notes ?? '', boundary_side: editing.boundary_side ?? '', neighbour_farmer_id: editing.neighbour_farmer_id ?? '',
    is_indigenous: (editing.is_indigenous == null ? '' : editing.is_indigenous ? 'yes' : 'no') as '' | 'yes' | 'no',
    indigenous_community: editing.indigenous_community ?? '',
    level: (editing.farmer_id ? 'farmer' : 'organisation') as 'organisation' | 'farmer',
  } : { ...EMPTY, farmer_id: farmerId ?? '', plot_id: plotId ?? '', relation_to_farm: neighbour ? 'adjoining_land' : '' });
  const orgLevel = form.level === 'organisation' && !farmerId && !plotId && !neighbour;
  const isNeighbour = form.relation_to_farm === 'adjoining_land';
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // A saved record opens read-only; Edit unlocks it (owner, 2026-09-27).
  const [viewing, setViewing] = useState(Boolean(editing));
  const editingId = editing?.id;
  useEffect(() => { setViewing(Boolean(editingId)); }, [editingId]);
  const existingPhoto = usePhotoUrls([editing?.photo_document_id]);

  const roles = useQuery({ queryKey: ['ref', 'stakeholder_role'], queryFn: () => referenceValues('stakeholder_role'), enabled: isSupabaseConfigured && open });
  const farmers = useQuery({ queryKey: ['picker', 'farmers'], queryFn: () => farmerOptions(), enabled: isSupabaseConfigured && open && (!farmerId || isNeighbour) });
  const plots = useQuery({
    queryKey: ['picker', 'plots', form.farmer_id], queryFn: () => plotOptions(form.farmer_id || undefined),
    enabled: isSupabaseConfigured && open && Boolean(form.farmer_id),
  });

  function pickPhoto(file: File) {
    if (!file.type.startsWith('image/')) { toast.error('Choose a photograph.'); return; }
    setPhoto(file);
    setPreview((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(file); });
  }

  async function save() {
    const phone = form.phone.trim() ? normaliseIdentifier('sms', form.phone.trim()) : null;
    const email = form.email.trim() ? normaliseIdentifier('email', form.email.trim()) : null;
    const ok = check({
      full_name: form.full_name.trim() ? undefined : 'Give the stakeholder\'s name.',
      neighbour_farmer_id: form.neighbour_farmer_id && form.neighbour_farmer_id === form.farmer_id
        ? 'A farmer cannot be their own neighbour.' : undefined,
      phone: phone && 'error' in phone ? phone.error : undefined,
      email: email && 'error' in email ? email.error : undefined,
      farmer_id: orgLevel || form.farmer_id ? undefined : 'Which farm (farmer) is this stakeholder for? Or choose Organisation level.',
    });
    if (!ok) return;
    setSaving(true);
    try {
      const values = {
        full_name: form.full_name.trim(),
        stakeholder_type: form.stakeholder_type,
        role_type_id: form.role_type_id || null,
        relation_to_farm: form.relation_to_farm || null,
        organisation_name: form.organisation_name.trim() || null,
        phone: phone && 'value' in phone ? phone.value : null,
        email: email && 'value' in email ? email.value : null,
        farmer_id: orgLevel ? null : form.farmer_id || null,
        plot_id: orgLevel ? null : form.plot_id || null,
        is_indigenous: form.is_indigenous === '' ? null : form.is_indigenous === 'yes',
        indigenous_community: form.is_indigenous === 'yes' ? form.indigenous_community.trim() || null : null,
        interest: form.interest.trim() || null,
        last_consulted_on: form.last_consulted_on || null,
        notes: form.notes.trim() || null,
        boundary_side: isNeighbour ? form.boundary_side || null : null,
        neighbour_farmer_id: isNeighbour ? form.neighbour_farmer_id || null : null,
      };
      const saved = editing
        ? await saveRow<StakeholderRow>('farm_stakeholders', editing.id, values)
        : await createRow<StakeholderRow>('farm_stakeholders', values);
      if (photo && profile?.organization) {
        try {
          const document = await uploadDocument({
            file: photo, bucket: 'farmer-photos', organizationId: profile.organization.id, ownerId: saved.id,
          });
          await saveRow('farm_stakeholders', saved.id, { photo_document_id: document.id });
        } catch (error) {
          toast.error(`Saved, but the photo was not: ${error instanceof Error ? error.message : 'upload failed'}.`);
        }
      }
      await queryClient.invalidateQueries({ queryKey: ['farm_stakeholders'] });
      toast.success(editing ? 'Saved.' : isNeighbour ? 'Neighbour added.' : 'Stakeholder added.');
      clear();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <RecordDrawer
      footer={
        <>
          {editing && (
            <DeleteRecordButton
              deleteFn={(reason) => saveRow('farm_stakeholders', editing.id, { is_deleted: true, delete_reason: reason })}
              entity="stakeholder" id={editing.id} invalidate={['farm_stakeholders']}
              label={`stakeholder ${editing.full_name}`} onDeleted={onClose} screenKey="party.register" />
          )}
          {viewing ? (
            <>
              <button onClick={onClose} type="button">Close</button>
              {mayWrite && <button className="primary" onClick={() => setViewing(false)} type="button"><Pencil size={14} /> Edit</button>}
            </>
          ) : (
            <>
              <button className="cancel" onClick={onClose} type="button">Cancel</button>
              {mayWrite && (<button className="primary" disabled={saving} onClick={() => void save()} type="button">
                {saving ? 'Saving...' : editing ? 'Save changes' : neighbour ? 'Add neighbour' : 'Add stakeholder'}
              </button>
              )}
            </>
          )}
        </>
      }
      onClose={onClose}
      open={open}
      record={editing ? { table: 'party.farm_stakeholders', id: editing.id, invalidate: ['farm_stakeholders'] } : null}
      subtitle={editing?.stakeholder_code ?? 'Someone with a stake in this farm: a neighbour, an official, a path user...'}
      title={editing ? editing.full_name : neighbour ? 'Add neighbour' : 'Add stakeholder'}
    >
      <ViewModeFieldset viewing={viewing}>
      <div className="form-photo">
        <FarmerPhoto name={form.full_name} size={72}
                     url={preview ?? (editing?.photo_document_id ? existingPhoto.data?.[editing.photo_document_id] : null)} />
        <div className="form-photo-actions">
          <DropZone accept="image/*" buttonLabel={preview || editing?.photo_document_id ? 'Change photo' : 'Add photo'}
                    label="Drag a photo here, or" onFile={pickPhoto} />
        </div>
      </div>
      {isNeighbour && (
        <div className="field-row">
          <Field hint="Which side of this plot their land lies on." label="Boundary side">
            <Select onChange={(value) => setForm({ ...form, boundary_side: value })} options={BOUNDARY_SIDES} value={form.boundary_side} />
          </Field>
          <Field error={errors.neighbour_farmer_id} hint="If the neighbour is also a registered member." label="Neighbour is our farmer">
            <Select
              onChange={(value) => {
                const picked = farmers.data?.find((row) => row.id === value);
                setForm({ ...form, neighbour_farmer_id: value, full_name: picked && !form.full_name.trim() ? picked.label.replace(/\s*\(.*\)$/, '') : form.full_name });
              }}
              options={[{ id: '', label: 'Not a member' }, ...(farmers.data ?? []).filter((row) => row.id !== form.farmer_id)]}
              placeholder={farmers.isLoading ? 'Loading...' : undefined} value={form.neighbour_farmer_id} />
          </Field>
        </div>
      )}
      <Field error={errors.full_name} label="Name" required>
        <input onChange={(event) => setForm({ ...form, full_name: event.target.value })} value={form.full_name} />
      </Field>
      <div className="field-row">
        <Field label="Stakeholder type">
          <Select onChange={(value) => setForm({ ...form, stakeholder_type: value })} options={STAKEHOLDER_TYPES} value={form.stakeholder_type} />
        </Field>
        <Field label="Role">
          <Select onChange={(value) => setForm({ ...form, role_type_id: value })}
                  options={(roles.data ?? []).map((row) => ({ id: row.id, label: row.label }))}
                  placeholder={roles.isLoading ? 'Loading...' : 'Select role'} value={form.role_type_id} />
        </Field>
      </div>
      <div className="field-row">
        <Field label="Relation to the farm">
          <Select onChange={(value) => setForm({ ...form, relation_to_farm: value })} options={RELATIONS} value={form.relation_to_farm} />
        </Field>
        <Field hint="Department, company, NGO or group, if any." label="Organisation">
          <input onChange={(event) => setForm({ ...form, organisation_name: event.target.value })} value={form.organisation_name} />
        </Field>
      </div>
      <div className="field-row">
        <Field error={errors.phone} label="Phone">
          <input inputMode="tel" onChange={(event) => setForm({ ...form, phone: event.target.value })} placeholder="98xxxxxx01" value={form.phone} />
        </Field>
        <Field error={errors.email} label="Email">
          <input inputMode="email" onChange={(event) => setForm({ ...form, email: event.target.value })} value={form.email} />
        </Field>
      </div>
      {!farmerId && !plotId && !neighbour && (
        <Field hint="Organisation level: a forest office, panchayat, NGO, buyer or union that concerns the whole programme." label="Level">
          <div className="view-toggle member-type" role="group" aria-label="Stakeholder level">
            {([['organisation', 'Organisation level'], ['farmer', 'Farmer level']] as const).map(([value, label]) => (
              <button aria-pressed={form.level === value} key={value} onClick={() => setForm({ ...form, level: value })} type="button">{label}</button>
            ))}
          </div>
        </Field>
      )}
      {!farmerId && !orgLevel && (
        <Field error={errors.farmer_id} label="Farm (farmer)" required>
          <Select onChange={(value) => setForm({ ...form, farmer_id: value, plot_id: '' })}
                  options={farmers.data ?? []} placeholder={farmers.isLoading ? 'Loading...' : 'Select farmer'} value={form.farmer_id} />
        </Field>
      )}
      {!plotId && !orgLevel && <Field hint="Only if the stake is in one plot - e.g. a path across it." label="Plot">
        <Select disabled={!form.farmer_id} onChange={(value) => setForm({ ...form, plot_id: value })}
                options={[{ id: '', label: 'Whole farm' }, ...(plots.data ?? [])]} value={form.plot_id} />
      </Field>}
      <div className="field-row">
        <Field hint="FSC Principle 3: indigenous peoples and their rights." label="Indigenous people (ST)?">
          <div className="view-toggle member-type" role="group" aria-label="Indigenous">
            {([['yes', 'Yes'], ['no', 'No'], ['', 'Not recorded']] as const).map(([value, label]) => (
              <button aria-pressed={form.is_indigenous === value} key={label} onClick={() => setForm({ ...form, is_indigenous: value })} type="button">{label}</button>
            ))}
          </div>
        </Field>
        {form.is_indigenous === 'yes' && (
          <Field label="Community / tribe">
            <input onChange={(event) => setForm({ ...form, indigenous_community: event.target.value })} placeholder="e.g. Santhal" value={form.indigenous_community} />
          </Field>
        )}
      </div>
      {isNeighbour && form.neighbour_farmer_id && form.is_indigenous === '' && (
        <p className="muted" style={{ margin: '-4px 0 8px' }}>Left blank, a member neighbour takes the answer on their own farmer record.</p>
      )}
      <Field hint="What their stake is, in a line." label="Interest">
        <input onChange={(event) => setForm({ ...form, interest: event.target.value })}
               placeholder="e.g. Uses the footpath along the east boundary to reach the river" value={form.interest} />
      </Field>
      <div className="field-row">
        <Field label="Last consulted on">
          <input max={todayIso()} onChange={(event) => setForm({ ...form, last_consulted_on: event.target.value })} type="date" value={form.last_consulted_on} />
        </Field>
      </div>
      <Field label="Notes">
        <textarea onChange={(event) => setForm({ ...form, notes: event.target.value })} rows={3} value={form.notes} />
      </Field>
      </ViewModeFieldset>
    </RecordDrawer>
  );
}
